import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Finance & procurement",
  title: "Invoice matching agent",
  cardSummary:
    "Three-way match of purchase order, delivery order and invoice before a payment goes out.",
  heroSubhead:
    "We design, build and deploy an AI agent that reads the supplier invoice, pulls the matching purchase order and delivery order, and runs the three-way match line by line — flagging price, quantity and tax mismatches before anything reaches the payment run, while a human still approves what gets paid.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Stops overpayments and duplicate payments before the money leaves — every Ringgit caught is cash kept, not time saved — and clears the AP backlog that holds up suppliers.",
    complexityLabel: "Medium",
    complexityWhy:
      "Vision/OCR on varied supplier invoices and a three-way reconciliation across three systems, but a fixed prompt-chain and read-only-until-approval keep autonomy and the eval bar contained.",
    timeToLive: "~a few weeks (one entity / AP inbox)",
    bestFit: "Finance & procurement teams running three-way match before payment",
  },
  outcomes: [
    "Overpayments and duplicate invoices caught before the money leaves",
    "AP backlog cleared — suppliers paid on time, early-payment discounts captured",
    "Every invoice matched consistently, not just the ones a clerk had time for",
    "More invoices processed month-end without adding headcount",
  ],
  impact: {
    manualLabel: "Matching an invoice by hand",
    manualValue: "~8–12 min",
    agentLabel: "With the agent",
    agentValue: "~1–2 min review",
    saved:
      "≈ 7–10 minutes saved per invoice — about 60–100 hours a month at 500–800 invoices. And every overpayment or duplicate it catches is cash kept: on a typical AP book, one prevented payment error a week can outweigh the whole AI cost for the month.",
    note: "Indicative; actual time and savings depend on invoice volume, how many line items each carries, and how clean your PO/DO data is.",
  },
  businessProblem:
    "An accounts-payable team in the region receives supplier invoices by email and post, then has to match each one against the purchase order it was raised for and the delivery order that proves the goods arrived. Most of a clerk's day is the mechanical part — keying the invoice, finding the right PO, checking every line's price and quantity, reconciling SST/GST — not deciding whether to pay. It's slow, it clogs at month-end, and under pressure clerks match on totals and wave through the lines, which is exactly how overpayments and duplicate invoices slip into the payment run.",
  manualWorkflow: {
    steps: [
      "Open the supplier invoice (PDF, scan or email)",
      "Key the header and every line item by hand",
      "Find the matching purchase order and delivery order",
      "Check each line's price, quantity and tax against both",
      "Chase the buyer or supplier on any mismatch",
      "Only then release it for approval and payment",
    ],
    breaks:
      "The bottleneck is line-by-line reconciliation across three documents — the slow, repetitive part that gets short-cut at month-end, and short-cutting it is precisely how an overpayment or a duplicate gets paid.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a prompt-chaining workflow: each invoice runs through a short, reliable chain — read the invoice → pull its PO and DO → match line by line → flag the exceptions → assemble an approval-ready packet — with the agent only stopping to ask a human when a line genuinely doesn't reconcile.",
    steps: [
      "Read the supplier invoice (incl. scans)",
      "Pull the matching PO & delivery order",
      "Three-way match line by line",
      "Flag price, quantity & tax exceptions",
      "Assemble an approval-ready packet",
    ],
    inLabel: "An invoice arrives",
    outLabel: "An approval-ready match packet",
    loopAround: {
      index: 2,
      label:
        "Re-reads the line and re-pulls the source document only when a line fails to reconcile — otherwise it keeps moving.",
    },
    humanStep: "Your AP approver releases (or holds) the payment",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the invoice bundle",
    inMessage:
      "Invoice INV-4471 (Supplier: Tenaga Supplies): 100 units @ RM 12.00 = RM 1,200.00. PO-0392: 100 units @ RM 11.50. DO-0392: 96 units received. SST/GST 10% applied on invoice.",
    outLabel: "What the agent flags",
    outLines: [
      "Supplier, PO number and currency match across all three documents",
      "Unit price mismatch: invoiced RM 12.00 vs PO RM 11.50 (RM 0.50/unit over)",
      "Quantity mismatch: invoiced 100 vs 96 received on the DO (short-delivered 4)",
      "SST/GST rate and calculation check out",
    ],
    outResult:
      "2 exceptions flagged — RM 98.00 overbilled (price + 4 undelivered units); held for approval, not paid",
    note: "Every flag cites the exact document, line and figure · your approver releases the payment · ~RM 0.40–0.90 per invoice in AI cost.",
  },
  workflowDetail: [
    {
      title: "Read the invoice",
      detail:
        "The agent ingests the supplier invoice — native PDF, scan, or an emailed image — reading the header (supplier, invoice number, date, PO reference, tax) and every line item, using vision for scans and photographed documents.",
    },
    {
      title: "Pull the PO and delivery order",
      detail:
        "Using the PO reference on the invoice, it retrieves the matching purchase order and the delivery order (goods-received note) from your ERP or document store — the two source-of-truth documents the invoice must agree with.",
    },
    {
      title: "Three-way match, line by line",
      detail:
        "It reconciles each invoice line against the PO (agreed price and quantity ordered) and the DO (quantity actually received): unit price, ordered vs received vs billed quantity, line and total amounts, and the SST/GST treatment.",
    },
    {
      title: "Evaluate and re-check (the loop)",
      detail:
        "Where a line doesn't reconcile or confidence is low, it re-reads that line or re-pulls the source document before finalising — so a genuine mismatch is confirmed, not a mis-read, before it's flagged.",
    },
    {
      title: "Flag exceptions and assemble the packet",
      detail:
        "It produces an approval-ready packet: the matched lines, the two or three exceptions to look at — each with the amount over/under and the exact document it came from — and a recommended hold or release.",
    },
    {
      title: "Hand to a human",
      detail:
        "Your AP approver reviews the exceptions and releases or holds the payment. Nothing is paid automatically.",
    },
  ],
  howWeBuild:
    "It works from your live PO and delivery-order data — not a stale export — so it matches against what was actually ordered and received, and it re-reads the goods-received quantity right before it finalises. It writes a structured match packet through connected tools instead of touching your payment run directly, so it can flag and recommend but never release money on its own — and every exception it raises traces back to the exact document, line and figure it came from.",
  agentLoop: [
    "the invoice + its live PO & delivery-order data",
    "read, retrieve & three-way match (MCP tools)",
    "re-check each exception against the source before surfacing",
  ],
  integrations: [
    "ERP / accounts-payable system (PO, invoice, GRN)",
    "Document store or AP intake inbox",
    "Optional: supplier master & tax (SST/GST / e-Invoice) reference",
  ],
  architecture: [
    "Pattern: a prompt-chain on the Claude Agent SDK — each tool exposed via an in-process MCP server; read the invoice → retrieve PO/DO → match → flag → assemble packet.",
    "Context: the invoice plus its live PO and delivery-order records are loaded each run — text plus vision for scans — with the goods-received quantity re-read right before the packet is finalised so it never matches against a stale copy.",
    "Tools: read-only PO/DO/supplier-master lookups, a vision/OCR line extractor, a deterministic line-matcher, and one write tool that only appends a structured match packet to the AP review queue — no tool can touch the payment run.",
    "Guardrails: least-privilege and read-only into the ERP; the agent flags and recommends but cannot release, edit or schedule a payment; schema-locked JSON output; a duplicate-invoice check (supplier + invoice number + amount) runs before anything is queued.",
    "Untrusted input & PDPA/PDPC: invoice text is treated as untrusted (a doctored PDF can't steer the match), and supplier bank details, IC/business-registration numbers and pricing are handled PDPA/PDPC-aligned.",
    "Recovery & escalation: on a failed reconciliation, a missing PO/DO, or low OCR confidence it re-reads or re-pulls, then routes the invoice to a human queue — never a silent match, never a guessed figure.",
    "Observability & audit: every tool call, the retrieved source figures, and each exception are logged and traceable, with the approver's release/hold captured against the packet — a machine-readable audit trail suited to Finance controls and an external audit.",
    "Eval harness: a golden set of real historical invoices — including short-deliveries, price creep, tax errors and known duplicates — with deterministic line checks plus an LLM-as-judge on ambiguous cases, regression-tested on every change before it touches a live payment run.",
  ],
  models: [
    {
      task: "Read & extract invoice lines (incl. scans)",
      model: "Claude Sonnet",
      why: "Reliable vision and structured extraction on varied, real-world supplier invoice layouts.",
    },
    {
      task: "Three-way match & reason across documents",
      model: "Claude Sonnet",
      why: "The workhorse — strong reasoning at moderate cost; handles the bulk of invoices.",
    },
    {
      task: "High-volume line & duplicate checks",
      model: "Claude Haiku",
      why: "Cheapest and fastest for the many simple line-level and duplicate yes/no checks.",
    },
    {
      task: "Resolve ambiguous / disputed exceptions",
      model: "Claude Opus",
      why: "Invoked only when Sonnet flags low confidence on a tricky reconciliation — a small fraction of invoices.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per invoice",
        value: "~RM 0.40–0.90",
        note: "invoice + PO/DO context + reasoning + output packet, at Claude Sonnet token rates",
      },
      {
        label: "AI usage — at 800 invoices/month",
        value: "~RM 280–630 / month",
        note: "roughly half with the Batch API for overnight, non-urgent runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting your ERP, document store and AP queue is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to a single prevented overpayment or duplicate a month",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on invoice sizes, line-item counts and monthly volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  modelChoice: {
    approach: "Single model",
    rationale:
      "A three-way match is sequential and shares one case context — the invoice and its two source documents — so a single well-instrumented model is the right call: reliable, cheaper per invoice, and easier to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single, tightly-coupled reconciliation.",
  },
  evaluation: [
    "Deterministic checks first — price match, quantity match (ordered/received/billed), tax and totals",
    "Self-verification — the agent re-checks each exception against the source before surfacing it",
    "Business KPIs — AP time per invoice, exception catch rate, overpayments prevented, throughput",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current three-way match on a sample — time per invoice, how consistently two clerks reach the same result, and how many exceptions slip through to payment. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right match outcome?",
        metrics: [
          {
            name: "Match decision vs a senior AP clerk",
            measures:
              "Its matched/held decision and the exceptions it raises agree with an expert's on held-out invoices",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the reconciliation sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures:
              "Every mandated check is run per line — price, quantity, tax, duplicate — nothing skipped",
            target: "100%",
          },
          {
            name: "No wasteful loops",
            measures: "It re-reads or re-pulls only when a line genuinely fails, not needlessly",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Line extraction accuracy",
            measures: "Did it read each line — description, quantity, unit price, tax — correctly?",
            target: "≥ 98%",
          },
          {
            name: "Exception recall (catch rate)",
            measures: "Of the real mismatches in the golden set, how many it flags",
            target: "≥ 97%",
          },
          {
            name: "False-flag rate",
            measures: "How often it flags a line that actually reconciles",
            target: "≤ 8%",
          },
          {
            name: "No hallucinated figures",
            measures: "Every amount in the packet traces to a source document line",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not touch a live payment run until it beats the human baseline and clears these GPA targets on a golden set of historical invoices — including short-deliveries, price creep and known duplicates. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical invoices — including the mismatched and duplicated ones — and prove reliability before it touches a live payment run. A focused single-entity rollout is a matter of weeks.",
  ctaTitle: "Is your AP team matching invoices by hand?",
  ctaDescription:
    "Tell us your invoice volume and ERP and we'll tell you honestly whether an invoice matching agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does the agent pay invoices?",
      answer:
        "No. It reads, matches and flags, and assembles an approval-ready packet; your AP approver releases or holds the payment. It has no access to your payment run — human-in-the-loop is essential for money going out.",
    },
    {
      question: "How does it handle SST/GST and e-Invoice / LHDN requirements?",
      answer:
        "It checks the tax treatment as part of the match and can cross-reference your tax and e-Invoice fields, and it keeps a full audit trail so any matched invoice can be reconstructed. Treat this as a starting point for your finance and compliance team, not tax or legal advice.",
    },
    {
      question: "What if the PO or delivery order is missing?",
      answer:
        "It doesn't guess. A missing PO or DO is itself an exception — the agent routes the invoice to a human queue with what it does have, rather than matching against an incomplete picture.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept for one entity or AP inbox is usually a matter of weeks; a full rollout takes longer, mostly because of ERP integration and getting your AP team comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Kewangan & perolehan",
  title: "Ejen pemadanan invois",
  cardSummary:
    "Padanan tiga hala pesanan belian, nota penghantaran dan invois sebelum bayaran dikeluarkan.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang membaca invois pembekal, menarik pesanan belian dan nota penghantaran yang sepadan, dan menjalankan padanan tiga hala baris demi baris — menandakan percanggahan harga, kuantiti dan cukai sebelum apa-apa sampai ke larian bayaran, sementara manusia tetap meluluskan apa yang dibayar.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Menghentikan bayaran lebih dan bayaran pendua sebelum wang keluar — setiap Ringgit yang ditangkap ialah tunai yang disimpan, bukan sekadar masa dijimatkan — dan mengurangkan tunggakan AP yang melengahkan pembekal.",
    complexityLabel: "Sederhana",
    complexityWhy:
      "Visi/OCR pada invois pembekal yang pelbagai dan rekonsiliasi tiga hala merentas tiga sistem, tetapi rantaian prompt yang tetap dan baca-sahaja-sehingga-kelulusan mengekang autonomi dan tahap penilaian.",
    timeToLive: "~beberapa minggu (satu entiti / peti masuk AP)",
    bestFit: "Pasukan kewangan & perolehan yang menjalankan padanan tiga hala sebelum bayaran",
  },
  outcomes: [
    "Bayaran lebih dan invois pendua ditangkap sebelum wang keluar",
    "Tunggakan AP dikurangkan — pembekal dibayar tepat masa, diskaun bayaran awal ditangkap",
    "Setiap invois dipadankan secara konsisten, bukan hanya yang sempat dibuat kerani",
    "Lebih banyak invois diproses pada hujung bulan tanpa menambah kakitangan",
  ],
  impact: {
    manualLabel: "Padan invois secara manual",
    manualValue: "~8–12 min",
    agentLabel: "Dengan ejen",
    agentValue: "~1–2 min semak",
    saved:
      "≈ 7–10 minit dijimatkan setiap invois — kira-kira 60–100 jam sebulan pada 500–800 invois. Dan setiap bayaran lebih atau pendua yang ditangkapnya ialah tunai yang disimpan: pada buku AP biasa, satu ralat bayaran yang dicegah seminggu boleh mengatasi seluruh kos AI untuk sebulan.",
    note: "Anggaran; masa dan penjimatan sebenar bergantung pada volum invois, bilangan item baris setiap satu, dan sejauh mana bersih data PO/DO anda.",
  },
  businessProblem:
    "Pasukan akaun belum bayar (AP) sebuah syarikat di rantau ini menerima invois pembekal melalui e-mel dan pos, kemudian perlu memadankan setiap satu dengan pesanan belian yang mendasarinya dan nota penghantaran yang membuktikan barang telah tiba. Sebahagian besar hari kerani ialah bahagian mekanikal — menaip invois, mencari PO yang betul, menyemak harga dan kuantiti setiap baris, menyelaraskan SST/GST — bukan menilai sama ada untuk membayar. Ia perlahan, tersekat pada hujung bulan, dan di bawah tekanan kerani memadan pada jumlah dan meluluskan baris begitu sahaja, yang tepatnya cara bayaran lebih dan invois pendua menyelinap masuk ke larian bayaran.",
  manualWorkflow: {
    steps: [
      "Buka invois pembekal (PDF, imbasan atau e-mel)",
      "Taip pengepala dan setiap item baris secara manual",
      "Cari pesanan belian dan nota penghantaran yang sepadan",
      "Semak harga, kuantiti dan cukai setiap baris terhadap kedua-duanya",
      "Kejar pembeli atau pembekal atas mana-mana percanggahan",
      "Barulah lepaskan untuk kelulusan dan bayaran",
    ],
    breaks:
      "Halangan ialah rekonsiliasi baris demi baris merentas tiga dokumen — bahagian perlahan dan berulang yang dipintas pada hujung bulan, dan memintasnya tepatnya cara bayaran lebih atau pendua dibayar.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran prompt-chaining: setiap invois melalui rantaian pendek yang boleh dipercayai — baca invois → tarik PO dan DO → padan baris demi baris → tandakan pengecualian → himpunkan pakej sedia-kelulusan — dengan ejen hanya berhenti bertanya kepada manusia apabila satu baris benar-benar tidak sepadan.",
    steps: [
      "Baca invois pembekal (termasuk imbasan)",
      "Tarik PO & nota penghantaran yang sepadan",
      "Padanan tiga hala baris demi baris",
      "Tandakan pengecualian harga, kuantiti & cukai",
      "Himpunkan pakej sedia-kelulusan",
    ],
    inLabel: "Invois tiba",
    outLabel: "Pakej padanan sedia-kelulusan",
    loopAround: {
      index: 2,
      label:
        "Membaca semula baris dan menarik semula dokumen sumber hanya apabila satu baris gagal sepadan — jika tidak, ia terus bergerak.",
    },
    humanStep: "Pelulus AP anda melepaskan (atau menahan) bayaran",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam himpunan invois",
    inMessage:
      "Invois INV-4471 (Pembekal: Tenaga Supplies): 100 unit @ RM 12.00 = RM 1,200.00. PO-0392: 100 unit @ RM 11.50. DO-0392: 96 unit diterima. SST/GST 10% dikenakan pada invois.",
    outLabel: "Apa yang ejen tandakan",
    outLines: [
      "Pembekal, nombor PO dan mata wang sepadan merentas ketiga-tiga dokumen",
      "Percanggahan harga seunit: diinvois RM 12.00 vs PO RM 11.50 (RM 0.50/unit lebih)",
      "Percanggahan kuantiti: diinvois 100 vs 96 diterima pada DO (kurang hantar 4)",
      "Kadar dan pengiraan SST/GST betul",
    ],
    outResult:
      "2 pengecualian ditandakan — RM 98.00 terlebih bil (harga + 4 unit tidak dihantar); ditahan untuk kelulusan, tidak dibayar",
    note: "Setiap tanda memetik dokumen, baris dan angka tepat · pelulus anda melepaskan bayaran · ~RM 0.40–0.90 setiap invois dalam kos AI.",
  },
  workflowDetail: [
    {
      title: "Baca invois",
      detail:
        "Ejen membaca invois pembekal — PDF asli, imbasan, atau imej e-mel — membaca pengepala (pembekal, nombor invois, tarikh, rujukan PO, cukai) dan setiap item baris, menggunakan visi untuk imbasan dan dokumen difoto.",
    },
    {
      title: "Tarik PO dan nota penghantaran",
      detail:
        "Menggunakan rujukan PO pada invois, ia mendapatkan pesanan belian yang sepadan dan nota penghantaran (nota terima barang) daripada ERP atau simpanan dokumen anda — dua dokumen sumber-kebenaran yang mesti dipatuhi invois.",
    },
    {
      title: "Padanan tiga hala, baris demi baris",
      detail:
        "Ia menyelaraskan setiap baris invois terhadap PO (harga dipersetujui dan kuantiti dipesan) dan DO (kuantiti sebenar diterima): harga seunit, kuantiti dipesan vs diterima vs dibil, jumlah baris dan keseluruhan, dan layanan SST/GST.",
    },
    {
      title: "Nilai dan semak semula (gelung)",
      detail:
        "Apabila satu baris tidak sepadan atau keyakinan rendah, ia membaca semula baris itu atau menarik semula dokumen sumber sebelum memuktamadkan — supaya percanggahan sebenar disahkan, bukan salah baca, sebelum ditandakan.",
    },
    {
      title: "Tandakan pengecualian dan himpunkan pakej",
      detail:
        "Ia menghasilkan pakej sedia-kelulusan: baris yang dipadankan, dua atau tiga pengecualian untuk dilihat — setiap satu dengan jumlah lebih/kurang dan dokumen tepat asalnya — dan syor tahan atau lepas.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pelulus AP anda menyemak pengecualian dan melepaskan atau menahan bayaran. Tiada apa-apa dibayar secara automatik.",
    },
  ],
  howWeBuild:
    "Ia bekerja daripada data PO dan nota penghantaran langsung anda — bukan eksport lapuk — jadi ia memadan terhadap apa yang benar-benar dipesan dan diterima, dan ia membaca semula kuantiti terima barang tepat sebelum memuktamadkan. Ia menulis pakej padanan berstruktur melalui alat yang disambung dan bukan menyentuh larian bayaran anda secara langsung, jadi ia boleh menanda dan mengesyor tetapi tidak pernah melepaskan wang sendiri — dan setiap pengecualian yang dibangkitkan dijejak kembali ke dokumen, baris dan angka tepat asalnya.",
  agentLoop: [
    "invois + data PO & nota penghantaran langsungnya",
    "baca, dapatkan & padan tiga hala (alat MCP)",
    "semak semula setiap pengecualian terhadap sumber sebelum memaparkan",
  ],
  integrations: [
    "Sistem ERP / akaun belum bayar (PO, invois, GRN)",
    "Simpanan dokumen atau peti masuk AP",
    "Pilihan: rujukan induk pembekal & cukai (SST/GST / e-Invois)",
  ],
  architecture: [
    "Corak: rantaian prompt atas Claude Agent SDK — setiap alat didedahkan melalui pelayan MCP dalam-proses; baca invois → dapatkan PO/DO → padan → tanda → himpun pakej.",
    "Konteks: invois serta rekod PO dan nota penghantaran langsungnya dimuatkan setiap larian — teks serta visi untuk imbasan — dengan kuantiti terima barang dibaca semula tepat sebelum pakej dimuktamadkan supaya ia tidak pernah memadan terhadap salinan lapuk.",
    "Alat: semakan PO/DO/induk-pembekal baca-sahaja, pengekstrak baris visi/OCR, pemadan baris deterministik, dan satu alat tulis yang hanya menambah pakej padanan berstruktur ke baris gilir semakan AP — tiada alat boleh menyentuh larian bayaran.",
    "Pagar keselamatan: keistimewaan-minimum dan baca-sahaja ke ERP; ejen menanda dan mengesyor tetapi tidak boleh melepaskan, mengedit atau menjadualkan bayaran; output JSON terkunci-skema; semakan invois-pendua (pembekal + nombor invois + jumlah) berjalan sebelum apa-apa dibaris-gilirkan.",
    "Input tidak dipercayai & PDPA/PDPC: teks invois dianggap tidak dipercayai (PDF diusik tidak boleh mengemudi padanan), dan butiran bank pembekal, nombor IC/pendaftaran perniagaan dan harga dikendalikan selaras PDPA/PDPC.",
    "Pemulihan & eskalasi: apabila rekonsiliasi gagal, PO/DO hilang, atau keyakinan OCR rendah ia membaca semula atau menarik semula, kemudian menghalakan invois ke baris gilir manusia — tiada padanan senyap, tiada angka diteka.",
    "Kebolehlihatan & audit: setiap panggilan alat, angka sumber yang didapatkan, dan setiap pengecualian dilog dan boleh dijejak, dengan lepas/tahan pelulus direkod terhadap pakej — jejak audit boleh-dibaca-mesin yang sesuai untuk kawalan Kewangan dan audit luar.",
    "Rangka penilaian: set emas invois sejarah sebenar — termasuk kurang hantar, kenaikan harga, ralat cukai dan pendua diketahui — dengan semakan baris deterministik serta LLM-sebagai-hakim untuk kes samar, diuji regresi pada setiap perubahan sebelum menyentuh larian bayaran langsung.",
  ],
  models: [
    {
      task: "Baca & ekstrak baris invois (termasuk imbasan)",
      model: "Claude Sonnet",
      why: "Visi yang boleh dipercayai dan ekstrak berstruktur pada susun atur invois pembekal dunia sebenar yang pelbagai.",
    },
    {
      task: "Padanan tiga hala & menaakul merentas dokumen",
      model: "Claude Sonnet",
      why: "Kuda kerja — penaakulan kuat pada kos sederhana; mengendalikan sebahagian besar invois.",
    },
    {
      task: "Semakan baris & pendua bervolum tinggi",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk banyak semakan ya/tidak peringkat baris dan pendua yang mudah.",
    },
    {
      task: "Selesaikan pengecualian samar / dipertikai",
      model: "Claude Opus",
      why: "Digunakan hanya apabila Sonnet menandakan keyakinan rendah pada rekonsiliasi rumit — sebahagian kecil invois.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap invois",
        value: "~RM 0.40–0.90",
        note: "invois + konteks PO/DO + penaakulan + pakej output, pada kadar token Claude Sonnet",
      },
      {
        label: "Penggunaan AI — pada 800 invois/bulan",
        value: "~RM 280–630 / bulan",
        note: "lebih kurang separuh dengan Batch API untuk larian bukan segera pada waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung ERP, simpanan dokumen dan baris gilir AP anda adalah beberapa minggu; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding satu bayaran lebih atau pendua yang dicegah sebulan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada saiz invois, bilangan item baris dan volum bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  modelChoice: {
    approach: "Model tunggal",
    rationale:
      "Padanan tiga hala adalah berurutan dan berkongsi satu konteks kes — invois dan dua dokumen sumbernya — jadi model tunggal yang lengkap adalah pilihan tepat: boleh dipercayai, lebih murah setiap invois, dan lebih mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu rekonsiliasi yang berkait rapat.",
  },
  evaluation: [
    "Semakan deterministik dahulu — padanan harga, padanan kuantiti (dipesan/diterima/dibil), cukai dan jumlah",
    "Pengesahan sendiri — ejen menyemak semula setiap pengecualian terhadap sumber sebelum memaparkannya",
    "KPI perniagaan — masa AP setiap invois, kadar tangkap pengecualian, bayaran lebih dicegah, daya pemprosesan",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur padanan tiga hala semasa anda pada sampel — masa setiap invois, sejauh mana konsisten dua kerani mencapai keputusan sama, dan berapa banyak pengecualian terlepas ke bayaran. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai hasil padanan yang betul?",
        metrics: [
          {
            name: "Keputusan padanan vs kerani AP kanan",
            measures:
              "Keputusan padan/tahannya dan pengecualian yang dibangkitkan bersetuju dengan pakar pada invois terpencil",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah rekonsiliasi kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures:
              "Setiap semakan wajib dijalankan setiap baris — harga, kuantiti, cukai, pendua — tiada dilangkau",
            target: "100%",
          },
          {
            name: "Tiada gelung membazir",
            measures: "Ia membaca semula atau menarik semula hanya apabila baris benar-benar gagal, bukan sia-sia",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan ekstrak baris",
            measures: "Adakah ia membaca setiap baris — perihalan, kuantiti, harga seunit, cukai — dengan betul?",
            target: "≥ 98%",
          },
          {
            name: "Recall pengecualian (kadar tangkap)",
            measures: "Daripada percanggahan sebenar dalam set emas, berapa yang ditandakan",
            target: "≥ 97%",
          },
          {
            name: "Kadar tanda palsu",
            measures: "Berapa kerap ia menandakan baris yang sebenarnya sepadan",
            target: "≤ 8%",
          },
          {
            name: "Tiada angka halusinasi",
            measures: "Setiap jumlah dalam pakej boleh dijejak ke baris dokumen sumber",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menyentuh larian bayaran langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas invois sejarah — termasuk kurang hantar, kenaikan harga dan pendua diketahui. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas invois sejarah sebenar anda — termasuk yang tidak sepadan dan pendua — dan membuktikan kebolehpercayaan sebelum ia menyentuh larian bayaran langsung. Pelancaran satu entiti yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan AP anda memadan invois secara manual?",
  ctaDescription:
    "Beritahu kami volum invois dan ERP anda, dan kami akan beritahu dengan jujur sama ada ejen pemadanan invois berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ejen membayar invois?",
      answer:
        "Tidak. Ia membaca, memadan dan menandakan, serta menghimpunkan pakej sedia-kelulusan; pelulus AP anda melepaskan atau menahan bayaran. Ia tiada akses ke larian bayaran anda — manusia-dalam-gelung penting untuk wang yang keluar.",
    },
    {
      question: "Bagaimana ia mengendalikan keperluan SST/GST dan e-Invois / LHDN?",
      answer:
        "Ia menyemak layanan cukai sebagai sebahagian daripada padanan dan boleh merujuk silang medan cukai dan e-Invois anda, dan ia mengekalkan jejak audit penuh supaya mana-mana invois yang dipadankan boleh dibina semula. Anggap ini sebagai titik permulaan untuk pasukan kewangan dan pematuhan anda, bukan nasihat cukai atau undang-undang.",
    },
    {
      question: "Bagaimana jika PO atau nota penghantaran hilang?",
      answer:
        "Ia tidak meneka. PO atau DO yang hilang itu sendiri adalah pengecualian — ejen menghalakan invois ke baris gilir manusia dengan apa yang ada, dan bukan memadan terhadap gambaran yang tidak lengkap.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu entiti atau peti masuk AP biasanya beberapa minggu; pelancaran penuh mengambil masa lebih lama, kebanyakannya kerana integrasi ERP dan membiasakan pasukan AP anda, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "财务与采购",
  title: "发票匹配智能体",
  cardSummary: "在付款前完成采购订单、送货单与发票的三方匹配。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，读取供应商发票、调取对应的采购订单与送货单，逐行执行三方匹配——在任何款项进入付款批次前标记价格、数量与税务差异——而放款仍由人来审批。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "在款项流出前拦截超付与重复付款——拦下的每一令吉都是留住的现金，而不只是节省的时间——并清理拖累供应商的应付账款积压。",
    complexityLabel: "中等",
    complexityWhy:
      "对多样供应商发票的视觉/OCR，以及跨三套系统的三方核对，但固定的提示链与「审批前只读」把自主性和评估门槛控制在有限范围内。",
    timeToLive: "约数周（单一实体 / 应付账款收件箱）",
    bestFit: "在付款前执行三方匹配的财务与采购团队",
  },
  outcomes: [
    "在款项流出前拦截超付与重复发票",
    "清理应付账款积压——按时支付供应商，抓住早付折扣",
    "每一张发票都被一致地匹配，而不只是文员来得及处理的那些",
    "月末在不增加人手的情况下处理更多发票",
  ],
  impact: {
    manualLabel: "人工匹配一张发票",
    manualValue: "约 8–12 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 1–2 分钟复核",
    saved:
      "每张发票约节省 7–10 分钟——每月 500–800 张约合 60–100 小时。而它拦下的每一笔超付或重复付款都是留住的现金：在一本典型的应付账款账上，每周防住一次付款错误，就可能超过整月的 AI 成本。",
    note: "为估算；实际时间与节省取决于发票数量、每张发票的行项目数，以及你的 PO/DO 数据有多干净。",
  },
  businessProblem:
    "区域内的一家公司的应付账款（AP）团队通过电子邮件和邮寄收到供应商发票，随后必须将每张发票与其对应的采购订单、以及证明货物已到的送货单进行匹配。文员一天的大部分时间花在机械性工作上——录入发票、找到正确的采购订单、逐行核对价格与数量、核对 SST/GST——而不是判断是否付款。这个过程缓慢，月末容易堵塞，而在压力下文员往往只看总额、放行各行，而这恰恰是超付与重复发票混进付款批次的方式。",
  manualWorkflow: {
    steps: [
      "打开供应商发票（PDF、扫描件或邮件）",
      "手动录入表头及每一个行项目",
      "找到对应的采购订单与送货单",
      "对照两者核对每行的价格、数量与税额",
      "就任何差异向采购人或供应商追问",
      "之后才放行以供审批与付款",
    ],
    breaks:
      "瓶颈在于跨三份文件逐行核对——这个缓慢重复的环节在月末会被走捷径，而走捷径正是超付或重复付款被支付出去的原因。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 prompt-chaining（提示链）方式构建：每张发票都经过一条简短可靠的链路——读取发票 → 调取其 PO 与 DO → 逐行匹配 → 标记异常 → 组装可审批的资料包——只有当某一行确实无法核对时，智能体才会停下来询问人。",
    steps: [
      "读取供应商发票（含扫描件）",
      "调取对应的 PO 与送货单",
      "逐行执行三方匹配",
      "标记价格、数量与税务异常",
      "组装可审批的资料包",
    ],
    inLabel: "一张发票进入",
    outLabel: "一份可审批的匹配资料包",
    loopAround: {
      index: 2,
      label: "仅当某一行无法核对时才重新读取该行并重新调取源文件——否则持续推进。",
    },
    humanStep: "你的应付账款审批人放行（或暂缓）付款",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在发票材料中",
    inMessage:
      "发票 INV-4471（供应商：Tenaga Supplies）：100 件 @ RM 12.00 = RM 1,200.00。PO-0392：100 件 @ RM 11.50。DO-0392：收到 96 件。发票按 SST/GST 10% 计税。",
    outLabel: "智能体标记出什么",
    outLines: [
      "供应商、PO 编号与币种在三份文件间一致",
      "单价不符：开票 RM 12.00 vs PO RM 11.50（每件多 RM 0.50）",
      "数量不符：开票 100 vs 送货单收到 96（少送 4 件）",
      "SST/GST 税率与计算无误",
    ],
    outResult:
      "标记 2 处异常——多开票 RM 98.00（价格 + 4 件未送达）；暂缓待审批，未付款",
    note: "每个标记都引用确切的文件、行与数字 · 由你的审批人放款 · 每张发票 AI 成本约 RM 0.40–0.90。",
  },
  workflowDetail: [
    {
      title: "读取发票",
      detail:
        "智能体读取供应商发票——原生 PDF、扫描件或邮件图片——读取表头（供应商、发票号、日期、PO 引用、税额）与每一个行项目，并对扫描件和拍照文件使用视觉能力。",
    },
    {
      title: "调取采购订单与送货单",
      detail:
        "它根据发票上的 PO 引用，从你的 ERP 或文档库调取对应的采购订单与送货单（收货单）——这两份是发票必须与之一致的权威源文件。",
    },
    {
      title: "逐行三方匹配",
      detail:
        "它将每一发票行与 PO（约定价格与订购数量）和 DO（实际收货数量）核对：单价、订购 vs 收货 vs 开票数量、行金额与总额，以及 SST/GST 处理方式。",
    },
    {
      title: "评估并复核（循环）",
      detail:
        "当某行无法核对或置信度低时，它会先重新读取该行或重新调取源文件再定稿——从而在标记前确认那是真正的差异，而非读错。",
    },
    {
      title: "标记异常并组装资料包",
      detail:
        "它生成一份可审批的资料包：已匹配的行、需要关注的两三处异常——每处都附带多付/少付的金额及其来源文件——以及暂缓或放行的建议。",
    },
    {
      title: "交给人处理",
      detail:
        "你的应付账款审批人审阅异常并放行或暂缓付款。没有任何款项会被自动支付。",
    },
  ],
  howWeBuild:
    "它基于你的实时 PO 与送货单数据工作——而非陈旧的导出文件——因此它对照的是真正订购和收到的内容，并在定稿前重新读取收货数量。它通过连接的工具写入一份结构化的匹配资料包，而不直接触碰你的付款批次，因此它能标记并提出建议，却绝不会自行放款——它提出的每一个异常都能追溯到其来源的确切文件、行与数字。",
  agentLoop: [
    "发票 + 其实时 PO 与送货单数据",
    "读取、调取并三方匹配（MCP 工具）",
    "在呈现前对照源文件复核每处异常",
  ],
  integrations: [
    "ERP / 应付账款系统（PO、发票、GRN）",
    "文档库或应付账款收件箱",
    "可选：供应商主数据与税务（SST/GST / e-Invoice）参照",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的提示链——每个工具通过进程内 MCP 服务器提供；读取发票 → 调取 PO/DO → 匹配 → 标记 → 组装资料包。",
    "上下文：发票及其实时 PO 与送货单记录每次运行都会载入——文本加上对扫描件的视觉识别——并在资料包定稿前重新读取收货数量，因此绝不会对照陈旧副本匹配。",
    "工具：只读的 PO/DO/供应商主数据查询、视觉/OCR 行提取器、确定性行匹配器，以及一个仅向应付账款审阅队列追加结构化匹配资料包的写入工具——没有任何工具能触碰付款批次。",
    "防护机制：对 ERP 采用最小权限且只读；智能体标记并提出建议，但无法放行、修改或安排付款；JSON 模式锁定输出；在任何内容入队前先运行重复发票检查（供应商 + 发票号 + 金额）。",
    "不可信输入与 PDPA/PDPC：发票文本被视为不可信（被篡改的 PDF 无法左右匹配），供应商银行信息、身份证/商业注册号与价格均按 PDPA/PDPC 处理。",
    "恢复与上报：当核对失败、缺少 PO/DO 或 OCR 置信度低时，它会重新读取或重新调取，然后把发票转入人工队列——绝不静默匹配，绝不臆测数字。",
    "可观测性与审计：每次工具调用、调取到的源数字与每处异常都会留痕且可追溯，审批人的放行/暂缓对应记录于资料包上——一条适用于财务管控与外部审计的机器可读审计轨迹。",
    "评估框架：一组真实历史发票的黄金集——含少送、价格蠕升、税务错误与已知重复——配合确定性行检查，并对模糊案例采用 LLM 作为评审，每次改动在接触实时付款批次前都做回归测试。",
  ],
  models: [
    {
      task: "读取并提取发票行（含扫描件）",
      model: "Claude Sonnet",
      why: "对多样的真实供应商发票版式具备可靠的视觉与结构化提取能力。",
    },
    {
      task: "跨文件三方匹配与推理",
      model: "Claude Sonnet",
      why: "主力模型——中等成本下的强推理能力，处理绝大多数发票。",
    },
    {
      task: "高频的行级与重复检查",
      model: "Claude Haiku",
      why: "对大量简单的行级与重复是/否检查最便宜、最快。",
    },
    {
      task: "处理模糊 / 有争议的异常",
      model: "Claude Opus",
      why: "仅在 Sonnet 对棘手核对标记低置信度时调用——占发票的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每张发票",
        value: "约 RM 0.40–0.90",
        note: "发票 + PO/DO 上下文 + 推理 + 输出资料包，按 Claude Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 800 张发票",
        value: "约 RM 280–630 / 月",
        note: "使用 Batch API 处理非紧急的夜间任务约可减半",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入你的 ERP、文档库与应付账款队列约需数周；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比每月防住的一笔超付或重复付款，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于发票大小、行项目数量与每月业务量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  modelChoice: {
    approach: "单一模型",
    rationale:
      "三方匹配是顺序进行、共享同一案件上下文（发票及其两份源文件）的，因此一个完善配置的单一模型是正确选择——可靠、每张发票成本更低、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非一个紧耦合的核对。",
  },
  evaluation: [
    "先做确定性检查——价格匹配、数量匹配（订购/收货/开票）、税额与总额",
    "自我验证——智能体在呈现前对照源文件复核每处异常",
    "业务 KPI——每张发票的应付账款处理时间、异常拦截率、防住的超付、处理量",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的三方匹配——每张发票耗时、两名文员得出相同结果的一致程度、以及有多少异常漏到付款环节。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否达成了正确的匹配结果？",
        metrics: [
          {
            name: "匹配决定（对比资深应付账款文员）",
            measures: "在留出发票上，其匹配/暂缓决定与提出的异常与专家一致",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "核对是否合理且完整？",
        metrics: [
          {
            name: "必需检查全部执行",
            measures: "每一项规定检查都逐行执行——价格、数量、税额、重复——不遗漏",
            target: "100%",
          },
          {
            name: "无无谓的循环",
            measures: "仅在某行确实未通过时才重新读取或重新调取，而非无端反复",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "行提取准确率",
            measures: "是否正确读取每一行——描述、数量、单价、税额？",
            target: "≥ 98%",
          },
          {
            name: "异常召回率（拦截率）",
            measures: "黄金集中真实的不符，有多少被标记出来",
            target: "≥ 97%",
          },
          {
            name: "误报率",
            measures: "多久会标记出实际上能核对上的行",
            target: "≤ 8%",
          },
          {
            name: "无凭空捏造的数字",
            measures: "资料包中的每个金额都能追溯到某一源文件行",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史发票黄金集（含少送、价格蠕升与已知重复）上超过人工基线并达到这些 GPA 目标之前，它不会接触任何实时付款批次。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史发票上做概念验证——包括不符与重复的案例——在接触实时付款批次前先证明可靠性。聚焦单一实体的落地只需数周。",
  ctaTitle: "你的应付账款团队还在用人工匹配发票吗？",
  ctaDescription:
    "告诉我们你的发票量与 ERP，我们会诚实告诉你发票匹配智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "智能体会支付发票吗？",
      answer:
        "不会。它负责读取、匹配和标记，并组装一份可审批的资料包；由你的应付账款审批人放行或暂缓付款。它无权访问你的付款批次——对于流出的款项，人在环路中至关重要。",
    },
    {
      question: "它如何处理 SST/GST 与 e-Invoice / LHDN 的要求？",
      answer:
        "它在匹配过程中检查税务处理，并可交叉引用你的税务与 e-Invoice 字段，同时保留完整的审计轨迹，让任何已匹配的发票都能被还原。请将此视为你财务与合规团队的起点，而非税务或法律意见。",
    },
    {
      question: "如果缺少采购订单或送货单怎么办？",
      answer:
        "它不会臆测。缺少 PO 或 DO 本身就是一处异常——智能体会带着现有的信息把发票转入人工队列，而不是对照一幅不完整的画面进行匹配。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一实体或应付账款收件箱的概念验证通常只需数周；全面落地需要更长时间，主要是因为 ERP 集成和让应付账款团队适应，而不是 AI 本身。",
    },
  ],
};

export const invoiceMatching: AgentUseCase = {
  structure: {
    slug: "invoice-matching",
    status: "available",
    pattern: "prompt-chaining",
    route: "solutions/agent-use-cases/invoice-matching",
    heroImage: "/images/agent-use-cases/invoice-matching-hero.jpg",
    cardImage: "/images/agent-use-cases/invoice-matching-card.jpg",
    relatedUseCases: ["cash-application", "credit-evaluation", "contract-review"],
    trackLocation: "agent_use_case_invoice_matching",
    valueScore: 4,
    complexityScore: 3,
  },
  content: { en, ms, zh },
};
